import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  MenuItem,
  IconButton,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import { useMutation, useQuery } from "@apollo/client"; 
import { useSelectedBusiness } from "../../../redux/store";
import {
  ADD_PAYMENT,
  GET_PAYMENT_METHODS,
  GET_PAYMENTS,
  UPDATE_PAYMENT,
  DELETE_PAYMENT,
} from "../../../graphql/queries";
import { validateEmail } from "../../../util/utils";
import { useTokenLoader } from "../../../util/tokenLoader";

 
const PaymentSettings = ({ isDisabled = false }) => {
  // run & expose your token‐refresh hook
  const { trigger: fetchToken } = useTokenLoader();
  const selectedBusiness = useSelectedBusiness(); 
  const { data: paymentMethodsData } = useQuery(GET_PAYMENT_METHODS);
  const { data: paymentsData, refetch: refetchPayments } = useQuery(
    GET_PAYMENTS,
    {
      variables: { businessUrl: selectedBusiness.businessBasic.url },
    }
  );

  const [addPayment] = useMutation(ADD_PAYMENT);
  const [updatePayment] = useMutation(UPDATE_PAYMENT);
  const [deletePayment] = useMutation(DELETE_PAYMENT);

  const [email, setEmail] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [error, setError] = useState(false);
  const [currentEdit, setCurrentEdit] = useState(null);
  const [sortBy, setSortBy] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  const [warningOpen, setWarningOpen] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");

  const [usedPaymentMethods, setUsedPaymentMethods] = useState(new Set());

  useEffect(() => {
    if (paymentsData) {
      const usedMethods = new Set(
        paymentsData.payments.map((p) => Number(p.paymentMethod.id))
      );
      setUsedPaymentMethods(usedMethods);
    }
  }, [paymentsData]);

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
    if (error) setError(false);
  };

  const handlePaymentMethodChange = (event) => {
    setPaymentMethod(event.target.value);
  };

  const handleSubmit = () => { 

    if (!validateEmail(email)) {
      setError(true);
      console.log("Invalid email format!");
      return;
    }

    if (!paymentMethod) {
      console.log("No payment method selected!");
      return;
    }

    if (currentEdit !== null) {
      updatePayment({
        variables: {
          id: currentEdit,
          identifier: email,
        },
      }).then(() => {
        refetchPayments();
        fetchToken();
        setCurrentEdit(null);
        setEmail("");
        setPaymentMethod("");
      });
    } else {
      if (usedPaymentMethods.has(Number(paymentMethod))) {
        const existingMethod = paymentsData.payments.find(
          (p) => p.paymentMethod.id === paymentMethod
        );
        setWarningMessage(
          `A ${existingMethod.paymentMethod.type} payment method already exists.`
        );
        setWarningOpen(true);
      } else {
        addPayment({
          variables: {
            businessId: Number(selectedBusiness.businessBasic.id),
            identifier: email,
            paymentMethodId: Number(paymentMethod),
          },
        }).then(() => {
          refetchPayments();
          fetchToken();
          setEmail("");
          setPaymentMethod("");
        });
      }
    }
  };

  const handleEdit = (payment) => {
    setCurrentEdit(payment.id);
    setEmail(payment.identifier);
    setPaymentMethod(payment.paymentMethod.id);
  };

  const openDeleteDialog = (payment) => {
    setPaymentToDelete(payment);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setPaymentToDelete(null);
  };

  const confirmDelete = () => {
    if (paymentToDelete) {
      deletePayment({
        variables: {
          id: paymentToDelete.id,
        },
      }).then(() => {
        refetchPayments();
        fetchToken();
        closeDeleteDialog();
      });
    }
  };

  const handleSort = (field) => {
    setSortBy(field);
  };

  return (
    <Box sx={{ padding: "16px" }}>
      <Typography variant="h6">
        {currentEdit !== null
          ? "Edit  Paypal Payment Email"
          : "Enter Paypal Payment Email and Method"}
      </Typography>

      {/* Payment Method Dropdown */}
      {paymentMethodsData && (
        <FormControl fullWidth sx={{ marginBottom: "16px" }}>
          <InputLabel id="payment-method-label">Payment Method</InputLabel>
          <Select
            labelId="payment-method-label"
            value={paymentMethod}
            onChange={handlePaymentMethodChange}
            label="Payment Method"
            disabled={currentEdit !== null}
          >
            {paymentMethodsData.paymentMethods.map((method) => (
              <MenuItem
                key={method.id}
                value={method.id}
                disabled={
                  isDisabled || (usedPaymentMethods.has(Number(method.id)) &&
                    currentEdit === null) ||
                  (currentEdit !== null && method.id !== paymentMethod)
                }
                sx={{
                  opacity:
                    (usedPaymentMethods.has(Number(method.id)) &&
                      currentEdit === null) ||
                    (currentEdit !== null && method.id !== paymentMethod)
                      ? 0.5
                      : 1,
                }}
              >
                {method.type}
                {usedPaymentMethods.has(Number(method.id)) &&
                currentEdit === null
                  ? " (Already Added)"
                  : ""}
                {currentEdit !== null && method.id === paymentMethod
                  ? " (Current)"
                  : ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Email Input */}
      <TextField
        label="Payment Email"
        value={email}
        onChange={handleEmailChange}
        error={error && !validateEmail(email)}
        helperText={
          error && !validateEmail(email) ? "Please enter a valid email" : ""
        }
        fullWidth
        sx={{ marginBottom: "16px" }}
      />

      {/* Save Button */}
      <Button
        variant="contained"
        onClick={handleSubmit}
        disabled={!email || !paymentMethod}
        sx={{ width: "100%" }}
      >
        {currentEdit !== null ? "Update Payment Email" : "Save Payment Method"}
      </Button>

      {/* Sort Options */}
      <FormControl fullWidth sx={{ marginTop: "16px", marginBottom: "16px" }}>
        <InputLabel>Sort By</InputLabel>
        <Select
          value={sortBy}
          onChange={(e) => handleSort(e.target.value)}
          label="Sort By"
        >
          <MenuItem value="paymentMethod">Payment Method</MenuItem>
          <MenuItem value="email">Email</MenuItem>
        </Select>
      </FormControl>

      {/* Display saved payments */}
      {paymentsData && (
        <Box sx={{ marginTop: "24px" }}>
          <Typography variant="h6">Saved Payment Methods:</Typography>
          {paymentsData.payments?.map((payment) => (
            <Box
              key={payment.id}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "8px",
              }}
            >
              <Typography>
                {payment.paymentMethod.type}: {payment.identifier}
              </Typography>
              <Box>
                <IconButton
                  onClick={() => handleEdit(payment)}
                  sx={{ marginRight: "8px" }}
                >
                  <Edit />
                </IconButton>
                <IconButton onClick={() => openDeleteDialog(payment)}>
                  <Delete />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Are you sure?</DialogTitle>
        <DialogContent>
          <Typography>
            Do you really want to delete this payment method?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>Cancel</Button>
          <Button onClick={confirmDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Warning Snackbar */}
      <Snackbar
        open={warningOpen}
        autoHideDuration={6000}
        onClose={() => setWarningOpen(false)}
      >
        <Alert
          onClose={() => setWarningOpen(false)}
          severity="warning"
          sx={{ width: "100%" }}
        >
          {warningMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PaymentSettings;
